"use client"

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { railStationCreateSchema } from '../../../../data/validators'

type StationFormValues = Record<string, unknown> & {
  code: string
  name: string
  nameInternational?: string | null
  city: string
  voivodeship?: string | null
  country: string
  latitude?: number | null
  longitude?: number | null
  stationType: string
  infrastructureManager: string
  source: string
  isElectrified?: boolean
  maxTrainLengthM?: number | null
  trackCount?: number | null
  hasCrane?: boolean
  hasWeighbridge?: boolean
  hasReachstacker?: boolean
  terminalCapacityTeu?: number | null
  lineNumbers?: string | null
  kmMarker?: number | null
}

export default function CreateStationPage() {
  const t = useT()
  const router = useRouter()

  const formSchema = React.useMemo(
    () => railStationCreateSchema.omit({ organizationId: true, tenantId: true }),
    [],
  )

  const stationTypeOptions = React.useMemo(() => [
    { value: 'FREIGHT_TERMINAL', label: t('rail_network.stations.types.FREIGHT_TERMINAL', 'Freight Terminal') },
    { value: 'PASSENGER', label: t('rail_network.stations.types.PASSENGER', 'Passenger') },
    { value: 'MIXED', label: t('rail_network.stations.types.MIXED', 'Mixed') },
    { value: 'SIDING', label: t('rail_network.stations.types.SIDING', 'Siding') },
    { value: 'PORT_TERMINAL', label: t('rail_network.stations.types.PORT_TERMINAL', 'Port Terminal') },
    { value: 'INLAND_TERMINAL', label: t('rail_network.stations.types.INLAND_TERMINAL', 'Inland Terminal') },
    { value: 'BORDER_CROSSING', label: t('rail_network.stations.types.BORDER_CROSSING', 'Border Crossing') },
  ], [t])

  const sourceOptions = React.useMemo(() => [
    { value: 'MANUAL', label: t('rail_network.stations.sources.MANUAL', 'Manual') },
    { value: 'UTK', label: t('rail_network.stations.sources.UTK', 'UTK') },
    { value: 'OSM', label: t('rail_network.stations.sources.OSM', 'OSM') },
  ], [t])

  const fields = React.useMemo<CrudField[]>(() => [
    { id: 'code', label: t('rail_network.stations.fields.code', 'Station Code (PKP/PLK)'), type: 'text', required: true, placeholder: 'e.g. GDNK' },
    { id: 'name', label: t('rail_network.stations.fields.name', 'Name'), type: 'text', required: true },
    { id: 'nameInternational', label: t('rail_network.stations.fields.nameInternational', 'International Name'), type: 'text' },
    { id: 'city', label: t('rail_network.stations.fields.city', 'City'), type: 'text', required: true },
    { id: 'voivodeship', label: t('rail_network.stations.fields.voivodeship', 'Voivodeship'), type: 'text' },
    { id: 'country', label: t('rail_network.stations.fields.country', 'Country Code'), type: 'text', required: true, placeholder: 'POL' },
    { id: 'latitude', label: t('rail_network.stations.fields.latitude', 'Latitude'), type: 'number', required: true },
    { id: 'longitude', label: t('rail_network.stations.fields.longitude', 'Longitude'), type: 'number', required: true },
    { id: 'stationType', label: t('rail_network.stations.fields.stationType', 'Station Type'), type: 'select', required: true, options: stationTypeOptions },
    { id: 'infrastructureManager', label: t('rail_network.stations.fields.infrastructureManager', 'Infrastructure Manager'), type: 'text', required: true, placeholder: 'PLK' },
    { id: 'source', label: t('rail_network.stations.fields.source', 'Source'), type: 'select', required: true, options: sourceOptions },
    { id: 'lineNumbers', label: t('rail_network.stations.fields.lineNumbers', 'Line Numbers'), type: 'text', placeholder: 'e.g. 131,271,3' },
    { id: 'kmMarker', label: t('rail_network.stations.fields.kmMarker', 'Kilometer Marker'), type: 'number' },
    { id: 'isElectrified', label: t('rail_network.stations.fields.isElectrified', 'Electrified'), type: 'checkbox' },
    { id: 'maxTrainLengthM', label: t('rail_network.stations.fields.maxTrainLength', 'Max Train Length (m)'), type: 'number' },
    { id: 'trackCount', label: t('rail_network.stations.fields.trackCount', 'Track Count'), type: 'number' },
    { id: 'hasCrane', label: t('rail_network.stations.fields.hasCrane', 'Has Crane'), type: 'checkbox' },
    { id: 'hasWeighbridge', label: t('rail_network.stations.fields.hasWeighbridge', 'Has Weighbridge'), type: 'checkbox' },
    { id: 'hasReachstacker', label: t('rail_network.stations.fields.hasReachstacker', 'Has Reachstacker'), type: 'checkbox' },
    { id: 'terminalCapacityTeu', label: t('rail_network.stations.fields.terminalCapacity', 'Terminal Capacity (TEU)'), type: 'number' },
  ], [sourceOptions, stationTypeOptions, t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'basic',
      title: t('rail_network.stations.groups.basic', 'Basic Information'),
      fields: ['code', 'name', 'nameInternational', 'city', 'voivodeship', 'country'],
    },
    {
      id: 'location',
      title: t('rail_network.stations.groups.location', 'Location'),
      fields: ['latitude', 'longitude', 'lineNumbers', 'kmMarker'],
    },
    {
      id: 'classification',
      title: t('rail_network.stations.groups.classification', 'Classification'),
      fields: ['stationType', 'infrastructureManager', 'source'],
    },
    {
      id: 'capabilities',
      title: t('rail_network.stations.groups.capabilities', 'Capabilities'),
      fields: ['isElectrified', 'maxTrainLengthM', 'trackCount', 'hasCrane', 'hasWeighbridge', 'hasReachstacker', 'terminalCapacityTeu'],
    },
  ], [t])

  const initialValues = React.useMemo<Partial<StationFormValues>>(() => ({
    country: 'POL',
    stationType: 'MIXED',
    infrastructureManager: 'PLK',
    source: 'MANUAL',
    isElectrified: true,
    hasCrane: false,
    hasWeighbridge: false,
    hasReachstacker: false,
  }), [])

  const handleSubmit = React.useCallback(async (values: StationFormValues) => {
    try {
      const { result } = await createCrud<{ id?: string }>(
        'rail_network/stations',
        values,
      )
      flash(t('rail_network.stations.createSuccess', 'Station created successfully'), 'success')
      const newId = result && typeof result.id === 'string' ? result.id : null
      if (newId) {
        router.push('/backend/rail-network')
      }
    } catch (err) {
      throw err
    }
  }, [router, t])

  return (
    <Page>
      <PageBody>
        <CrudForm<StationFormValues>
          title={t('rail_network.stations.createTitle', 'Add Rail Station')}
          fields={fields}
          groups={groups}
          initialValues={initialValues}
          onSubmit={handleSubmit}
          cancelHref="/backend/rail-network"
          backHref="/backend/rail-network"
          submitLabel={t('rail_network.stations.actions.save', 'Save Station')}
          schema={formSchema}
        />
      </PageBody>
    </Page>
  )
}
